import { FormulaValueJson, getWebPath } from '@doss/common-all'
import { Box, Typography } from '@mui/material'
import { isNil } from 'lodash'
import useStyles from '~/common/hooks/useStyles'
import { FormulaColumnChip } from '~/components/formula/FormulaColumnChip'
import { FormulaExpression } from '~/components/formula/FormulaExpression'
import { useFormulaContext } from '~/components/formula/FormulaProvider'
import { BreadcrumbObjectType } from '../../common/generated/apollo-operations'
import { Text } from '../ui/atoms/text/Text'
import { FormulaConditionExpression } from './FormulaConditionExpression'
import { FormulaKeyword } from './FormulaKeyword'

export interface FormulaValueExpressionProps {
  data: FormulaValueJson
}

function getStyles() {
  return {
    container: {
      display: 'flex',
      gap: '4px',
      alignItems: 'center',
    },
  }
}

/**
 * Renders a formula value
 */
export function FormulaValueExpression({ data }: FormulaValueExpressionProps) {
  const { columnChips, tableId } = useFormulaContext()
  const styles = useStyles(getStyles)

  if (data.tableColumn) {
    const chipIndex = columnChips.findIndex((chip) => chip.id === data.id)
    // If the column table is not a child table or the same table as the formula,
    // clicking on table name will route user to table
    const columnTableIsDifferentTableThanFormula = data.tableColumn.table?.id !== tableId
    const tablePath =
      data.tableColumn.table && isNil(data.tableColumn.table.parentTableId)
        ? getWebPath({
            objectType: BreadcrumbObjectType.TABLE,
            objectId: data.tableColumn.table.id,
          })
        : undefined
    const link = columnTableIsDifferentTableThanFormula ? (
      <>
        <Text href={tablePath} typography="body1" underline="hover" variant="link">
          {data.tableColumn.table?.name}
        </Text>{' '}
        <Text>&#8594; </Text>
      </>
    ) : undefined
    return (
      <Box sx={styles.container}>
        <FormulaColumnChip index={chipIndex} name={data.tableColumn.name} tableLink={link} />
      </Box>
    )
  }

  if (data.conditions.length > 0) {
    return (
      <Box sx={styles.container}>
        <Typography>
          <FormulaKeyword>IFS</FormulaKeyword>[
        </Typography>
        {data.conditions.map((formulaCondition, idx) => (
          <FormulaConditionExpression
            key={formulaCondition.id}
            data={formulaCondition}
            isFirst={idx === 0}
            isLast={idx === data.conditions.length - 1}
          />
        ))}
        <Typography>]</Typography>
      </Box>
    )
  }

  if (data.formula) {
    return (
      <Box sx={styles.container}>
        <Typography>(</Typography>
        <FormulaExpression data={data.formula} />
        <Typography>)</Typography>
      </Box>
    )
  }

  // String literals render quoted, matching how they are written in the formula grammar
  if (typeof data.valueString === 'string') {
    return (
      <Box sx={styles.container}>
        <Typography>
          <FormulaKeyword>{`"${data.valueString}"`}</FormulaKeyword>
        </Typography>
      </Box>
    )
  }

  if (!isNil(data.value)) {
    return (
      <Box sx={styles.container}>
        <Typography>
          <FormulaKeyword>{data.value}</FormulaKeyword>
        </Typography>
      </Box>
    )
  }

  return null
}
